<template>
    <ion-app
        :dir="direction"
        :style="{ backgroundImage: `url(${backgroundImage})` }"
        data-html2canvas-ignore
        @ionModalWillPresent="pauseAudio"
        @ionModalWillDismiss="pauseAudio"
        @ionActionSheetWillPresent="pauseAudio"
        @ionWillOpen="pauseAudio"
    >  
        
        <ion-router-outlet :animated="true" :animation="fadeTransition"/>
    </ion-app>
</template>

<script lang="ts">
import {createAnimation , type Animation, actionSheetController, menuController, IonApp, IonRouterOutlet, useBackButton } from "@ionic/vue";
import { defineComponent, ref, watch, computed, onMounted, onBeforeMount } from "vue";
import {NavOptions} from "@ionic/core";
import {useRoute, useRouter} from "vue-router";
import { useStore } from "vuex";
import translate from "./views/translate";
import MainMenu from "@/components/MainMenu.vue";
import {savePreferences, downloadManifestInBackground} from './storage/DataStorage';
import {openOnboardingPagesModal} from "@/services/InformationPagesService";
import {checkForJourneySetImport} from "@/utilities/JourneySet";
import {getImageDataUri} from "@/utilities/Compatibility";
import {AlertBuilder} from "@/utilities/AlertBuilder";
import {handleAndroidFileSelectToDelete, ensureFileFromAssets, ensureImageFromAssets, flushLogs} from "@/utilities/Filesystem";
import {MimeTypes} from "@/types/types";
import {App as capacitorApp} from '@capacitor/app';
import { App } from '@capacitor/app';
interface TransitionOptions {
  enteringEl: HTMLElement;
  leavingEl?: HTMLElement;
}

export default defineComponent({
    name: "App",
    components: {
        IonRouterOutlet,
        //MainMenu,
        IonApp,
    },
    setup() {
        const route = useRoute();
        const router = useRouter();
        const store = useStore();
        const { t } = translate();
        
        
        const currentPathName = ref(route.name);
        const currentPath = ref(route.path);
        const imageForHeader = computed(() => { return store.state.headerImage});
        const direction = computed(() => t("messages.language.textDirection"));
        const textForHeader = computed(() => store.state.headerTitle);
        const backgroundImage = ref('' as string);
        const isLargeViewport = window.innerWidth > 1024 || window.innerHeight > 1024;

        const fadeTransition = (baseEl: HTMLElement, opts: any): Animation => {
            const options = opts as TransitionOptions; // Type assertion to avoid errors
            const enteringPage = options.enteringEl;
            const leavingPage = options.leavingEl;

            const enteringAnimation = createAnimation()
                .addElement(enteringPage)
                .fromTo('opacity', '0', '1');

            const leavingAnimation = leavingPage
                ? createAnimation().addElement(leavingPage).fromTo('opacity', '1', '0')
                : null;

            const animation = createAnimation().addAnimation([enteringAnimation]);
            if (leavingAnimation) animation.addAnimation(leavingAnimation);

            return animation;
        };

        function rgbToHsl(rgbStr: string, lightnessIncrease: number = 20) {
            const rgbArray = rgbStr.split(",");
            let r = parseInt(rgbArray[0]);
            let g = parseInt(rgbArray[1]);
            let b = parseInt(rgbArray[2]);
            r /= 255;
            g /= 255;
            b /= 255;
            const max = Math.max(r, g, b), min = Math.min(r, g, b);
            let h = 0, s = 0, l = (max + min) / 2;

            if (max !== min) {
                const d = max - min;
                s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                switch (max) {
                    case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                    case g: h = (b - r) / d + 2; break;
                    case b: h = (r - g) / d + 4; break;
                }
                h *= 60;
            }

            // Increase lightness for tint
            l = l * 100;

            // Increase lightness but keep it in range (0-100%)
            l = Math.min(l + lightnessIncrease, 100);
            return `hsl(${Math.round(h)}, ${Math.round(s * 100)}%, ${Math.round(l)}%)`;
        }
        
        
        function initColors() {
            window.document.body.style.setProperty('--color-primary', `rgb(${t("messages.application.primaryColor")})`);
            window.document.body.style.setProperty('--color-primary-rgb', t("messages.application.primaryColor"));
            if(t("messages.application.secondaryColor") != "messages.application.secondaryColor"){
                window.document.body.style.setProperty('--color-secondary', `rgb(${t("messages.application.secondaryColor")})`);
                window.document.body.style.setProperty('--color-secondary-rgb', t("messages.application.secondaryColor"));
            }
            window.document.body.style.setProperty('--color-app-background-rgb',  `rgba(${t("messages.application.primaryColor")},.03)`);
            window.document.body.style.setProperty('--color-journey-card-background-hsl',  rgbToHsl(`${t("messages.application.primaryColor")}` , 86));
            window.document.body.style.setProperty('--color-journey-card-notdownloaded-background-hsl',  rgbToHsl(`${t("messages.application.primaryColor")}` , 86));
            window.document.body.style.setProperty('--color-card-background', `rgb(${t("messages.application.cardColor")})`);
            window.document.body.style.setProperty('--color-app-background-svg-fill-rgb',  `rgba(${t("messages.application.primaryColor")},.07)`);
            window.document.body.style.setProperty('--color-card-background-fill-rgb',  `rgba(${t("messages.application.primaryColor")},.05)`);
            window.document.body.style.setProperty('--color-footer-background-rgb',  `rgba(${t("messages.application.primaryColor")},.50)`);
            window.document.body.style.setProperty('--quadrant-field-1-background', t("messages.application.quadrantColors.quadrant1"));
            window.document.body.style.setProperty('--quadrant-field-2-background', t("messages.application.quadrantColors.quadrant2"));
            window.document.body.style.setProperty('--quadrant-field-3-background', t("messages.application.quadrantColors.quadrant3"));
            window.document.body.style.setProperty('--quadrant-field-4-background', t("messages.application.quadrantColors.quadrant4"));
            window.document.body.style.setProperty('--quadrant-field-5-background', t("messages.application.quadrantColors.quadrant5"));
            window.document.body.style.setProperty('--quadrant-field-6-background', t("messages.application.quadrantColors.quadrant6"));
        }

        function initSizes() {
            window.document.body.style.setProperty('--quadrant-font-size', t("messages.application.quadrantFontSize"));
            
            const journeyImgSize = t("messages.application.journeyImageSize");
            if (journeyImgSize === "medium") {
                window.document.body.style.setProperty('--journey-header-img-width', '30vh');
            } else if (journeyImgSize === "large") {
                window.document.body.style.setProperty('--journey-header-img-width', '50vh');
            } else {
                window.document.body.style.setProperty('--journey-header-img-width', '20vh');
            }
            if(store.state.platform !== 'android'){
                window.document.body.style.setProperty('--extra-footer-spacing', '0px');
            }
        }
        
        // Function to detect and set status bar height for Android
        const setStatusBarHeight = () => {
            if (store.getters.platform() === 'android') {
                // Multiple methods to detect status bar height
                let statusBarHeight = 0;
                
                // Method 1: Try CSS env() first
                const envValue = getComputedStyle(document.documentElement).getPropertyValue('--ion-safe-area-top');
                if (envValue && envValue !== '0px' && !envValue.includes('env(')) {
                    return; // CSS env() is working, don't override
                }
                
                // Method 2: Calculate from viewport differences
                const viewportHeight = window.innerHeight;
                const documentHeight = document.documentElement.clientHeight;
                statusBarHeight = viewportHeight - documentHeight;
                
                // Method 3: Use device pixel ratio for more accurate calculation
                if (statusBarHeight <= 0) {
                    console.log(`Device pixel ratio detected: ${window.devicePixelRatio}`);
                    const dpr = window.devicePixelRatio || 1;
                    // Common status bar heights in dp converted to px
                    if (dpr >= 3) {
                        statusBarHeight = 56; // High DPI devices like Pixel 5
                    } else if (dpr >= 2) {
                        statusBarHeight = 52; // Medium-high DPI
                    } else {
                        statusBarHeight = 34; // Standard DPI
                    }
                }
                
                // Method 4: Device-specific overrides based on user agent
                /* const userAgent = navigator.userAgent.toLowerCase();
                if (userAgent.includes('pixel')) {
                    statusBarHeight = Math.max(statusBarHeight, ); // Pixel devices typically have 36px
                }*/
                
                console.log(`Setting status bar height: ${statusBarHeight}px for device with DPR: ${window.devicePixelRatio}`);
                document.documentElement.style.setProperty('--ion-safe-area-top', `${statusBarHeight}px`);
            }
        };

        onBeforeMount(async () => {
            const { setLocaleMessage, t } = translate();
            const locale = store.getters.getLocale();
            const messages = store.getters.getLocaleMessages();
            
            if (store.getters.isFirstTimeOpeningApp()) {
                await ensureFileFromAssets('italic-font.txt' , 'assets/font/italic-font.txt');
                await ensureFileFromAssets('normal-font.txt' , 'assets/font/normal-font.txt');

                // Prime layout values before presenting first-launch onboarding so modal sizing is stable.
                if (!store.getters.platform()) {
                    await store.dispatch("setPlatform");
                }
                setLocaleMessage(locale, { messages: messages });
                if(store.getters.platform() === 'web'){
                    backgroundImage.value = messages.application.appBackgroundImagePWA;
                }else{
                    if (isLargeViewport) {
                        backgroundImage.value = messages.application.appBackgroundImagePWA;
                    } else {
                        backgroundImage.value = messages.application.appBackgroundImage;
                    }
                }
                if(!backgroundImage.value || backgroundImage.value.length < 20){
                    window.document.body.style.setProperty('--color-app-background-rgb',  `rgba(${t("messages.application.primaryColor")},0)`);
                }
                window.document.body.style.setProperty('--center-text-for-language', direction.value === 'ltr' ? 'center' : 'right' );
                initColors();
                initSizes();
                setStatusBarHeight();
                await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
                setStatusBarHeight();

                await openOnboardingPagesModal(messages);
                await store.dispatch("setIsFirstTimeOpeningApp", false);
                await deleteInstallFile(messages);
            } 
            await ensureImageFromAssets('appIcon.png', 'assets/img/appIcon.png');
            setLocaleMessage(locale, { messages: messages });
            
            // some header items need to be set here if they happen to be loading the app on these pages. this is due to having the images in the language file 
            if(route.name === 'security') {
                await store.dispatch("setHeaderImage", getImageDataUri(t("messages.securityPage.headerImage")))
            } else if(route.name === 'privacy'){
                await store.dispatch("setHeaderImage", getImageDataUri(t("messages.privacyPage.headerImage")))
            } else if(route.name === 'fields'){
                 await store.dispatch("setHeaderTitle", `${t("messages.application.appName")}`);
            }
            if(store.getters.platform() === 'web'){
                backgroundImage.value = messages.application.appBackgroundImagePWA;
                
            }else{
                if (isLargeViewport) {
                    backgroundImage.value = messages.application.appBackgroundImagePWA;
                } else {
                    backgroundImage.value = messages.application.appBackgroundImage;
                }
            }
            if(!backgroundImage.value || backgroundImage.value.length < 20){
                window.document.body.style.setProperty('--color-app-background-rgb',  `rgba(${t("messages.application.primaryColor")},0)`);
            }
            
            window.document.body.style.setProperty('--center-text-for-language', direction.value === 'ltr' ? 'center' : 'right' );
            initColors();
            initSizes();
            setStatusBarHeight();
        });
        
        onMounted(async () => {
            const preferences = store.getters.getPreferences();
            //Once the app is mounted, then store all preferences to the indexedDB
            //that may have been set or modified during initial app load
            await savePreferences(preferences);
            
            if (!preferences.shouldUseSafetyCalculator) {
                //store.dispatch("set")
                //console.log("shouldUseSafetyCalculator is false");
                await checkForJourneySetImport();
            }
            
            
            // Recalculate status bar height after DOM is fully loaded
            setTimeout(() => {
                setStatusBarHeight();
            }, 100);

            
            // Add resize listener to recalculate on orientation change
            window.addEventListener('resize', () => {
                setTimeout(() => {
                    setStatusBarHeight();
                }, 100);
            });
        })
        const shouldExitApp = computed(() =>store.getters.getShouldExitApp());
        
        capacitorApp.addListener("appStateChange", async (state: any) => {
            pauseAudio();
            actionSheetController.dismiss();
            menuController.close();
            const preferences = store.getters.getPreferences();
            const importingJourneySet = computed(() => store.getters.isImportingJourneySet());
            if (!state.isActive && (shouldExitApp.value === true )) {
                //console.log("App state is inactive and should exit app is true");
                if (preferences.shouldUseSafetyCalculator) {
                    router.push({ name: 'calculator'});
                }
                if(shouldExitApp.value === true && importingJourneySet.value === false){
                    //console.log("Exiting app");
                    capacitorApp.exitApp();
                }
            } else if(!state.isActive) {
                //console.log("App state is inactive but should exit app is false");
                await store.dispatch("setShouldExitApp", true);
                
                // Flush any buffered logs before backgrounding
                await flushLogs();
            } else if (state.isActive) {
                //console.log("App state is active");
                
                if (preferences.shouldUseSafetyCalculator && importingJourneySet.value === false) {
                    if (store.getters.getSuppressSafetyCalculatorOnResume()) {
                        await store.dispatch("setSuppressSafetyCalculatorOnResume", false);
                    } else {
                        router.push({ name: 'calculator'});
                    }
                }
            }

            if (state.isActive && store.getters.platform() === "ios") {
                await checkForJourneySetImport();
            }
            
            // TEMPORARILY DISABLED: Background download disabled for debugging iPhone crash
            // When app regains focus, trigger background download of journey set metadata
            // This runs in the background without blocking UI
            // IMPORTANT: Skip on first launch - the heavy DB operations crash the WebView on iPhones
            // Only run when user returns to an already-initialized app
            // if (state.isActive && !store.getters.isFirstTimeOpeningApp()) {
            //     // Delay to ensure app is fully resumed before starting background work
            //     setTimeout(() => {
            //         downloadManifestInBackground().catch(err => {
            //             console.error('[App] Background manifest download error:', err);
            //         });
            //     }, 3000);
            // }
            if (state.isActive) {
                console.log('[App.vue] BACKGROUND MANIFEST DOWNLOAD DISABLED FOR DEBUG');
            }
        });

        watch(route, (currentRoute) => {
            store.dispatch("setPrevPage", currentPathName.value == "calculator" ? "fields" : currentPath.value);
            currentPath.value = currentRoute.path;
            currentPathName.value = currentRoute.name;
        });

        // this is a global handler for capturing android back buttons on the calculator screen
        useBackButton(10, (processNextHandler) => {
            if (!currentPath.value.includes("calculator")) {
                processNextHandler();
            }
        });

        function pauseAudio() {
            store.dispatch("setShouldPauseAudio", true);
        }
        
        async function deleteInstallFile(messages: any) {
            if (store.getters.platform() === "android" && messages.buildConfigs.config.appStoreBuild === "false") {
                const alert = new AlertBuilder('custom-alert', `<p>${ messages.application.deleteInstallFile }</p>`)
                    .addNoButton()
                    .addYesButton(async () => {
                        await handleAndroidFileSelectToDelete(MimeTypes.APK);
                    });
                await alert.present();
            }
        }

        return {
            currentPath,
            imageForHeader,
            textForHeader,
            direction,
            backgroundImage,
            pauseAudio,
            fadeTransition
        };
    },
});
</script>

<style scoped>
ion-app {
  position: relative;
  width: 100%;
  height: 100dvh;
  /* background-repeat: no-repeat; */
  background-position: center;
  /* background-size: cover;  */
  background-color: var(--color-app-background-rgb);
}

.app-content-container {
  position: relative;
  z-index: 2; /* Ensures content stays above the overlay */
  width:100%;
  height:100dvh;
}
ion-app {
    max-width: 800px;
    margin: auto;
    box-shadow: 0 0 30px 0 rgba(0,0,0,.25);
    background: var(--color-app-background-rgb);
}

ion-app.ios {
    max-width: 100vw;
}

@media (min-width: 1024px), (min-height: 1024px) {
  ion-app {
    background-size: cover;
  }
}

ion-menu ion-content {
    --background: var(--ion-color-custom-step-0);
}

ion-menu.md ion-content {
    --padding-start: 8px;
    --padding-end: 8px;
    --padding-top: 20px;
    --padding-bottom: 20px;
}

ion-menu.md ion-list {
    padding: 20px 0;
}

ion-menu.md ion-note {
    margin-bottom: 30px;
}

ion-menu.md ion-list-header,
ion-menu.md ion-note {
    padding-left: 10px;
}

ion-menu.md ion-list#inbox-list {
    border-bottom: 1px solid var(--ion-color-custom-step-150, var(--menu-border));
}

ion-menu.md ion-list#inbox-list ion-list-header {
    font-size: 22px;
    font-weight: 600;
    min-height: 20px;
}

ion-menu.md ion-list#labels-list ion-list-header {
    font-size: 16px;
    margin-bottom: 18px;
    color: var(--menu-header-text);
    min-height: 26px;
}

ion-menu.md ion-item {
    --padding-start: 10px;
    --padding-end: 10px;
    border-radius: 4px;
}

ion-menu.md ion-item.selected {
    --background: rgba(var(--ion-color-primary-rgb), 0.14);
}

ion-menu.md ion-item.selected ion-icon {
    color: var(--ion-color-primary);
}

ion-menu.md ion-item ion-label {
    font-weight: 500;
}

ion-menu.ios ion-content {
    --padding-bottom: 20px;
}

ion-menu.ios ion-list {
    padding: 20px 0 0 0;
}

ion-menu.ios ion-note {
    line-height: 24px;
    margin-bottom: 20px;
}

ion-menu.ios ion-item {
    --padding-start: 16px;
    --padding-end: 16px;
    --min-height: 50px;
}

ion-menu.ios ion-item.selected ion-icon {
    color: var(--ion-color-primary);
}

ion-menu.ios ion-item ion-icon {
    font-size: 24px;
}

ion-menu.ios ion-list#labels-list ion-list-header {
    margin-bottom: 8px;
}

ion-menu.ios ion-list-header,
ion-menu.ios ion-note {
    padding-left: 16px;
    padding-right: 16px;
}

ion-menu.ios ion-note {
    margin-bottom: 8px;
}

ion-note {
    display: inline-block;
    font-size: 16px;

    color: var(--ion-color-medium-shade);
}

ion-item.selected {
    --color: var(--ion-color-primary);
}
</style>
